// Mixin for collecting and processing files from DOM elements
export const useFileCollector = controller => {
  Object.assign(controller, {
  collectTextContent() {
    const textParts = []
    
    this.textTargets.forEach(target => {
      const text = target.value?.trim() || target.textContent?.trim() || ''
      if (text) {
        textParts.push(text)
      }
    })

    return textParts.length > 0 ? textParts.join('\n\n') : null
  },

  async collectFiles() {
    const files = []

    for (const target of this.fileTargets) {
      try {
        let url = null

        if (target.tagName.toLowerCase() === 'img') {
          url = target.src
        } else if (target.tagName.toLowerCase() === 'a') {
          url = target.href
        }

        if (url) {
          const file = await this.fetchAsFile(url, target)
          if (file) {
            files.push(file)
          }
        }
      } catch (error) {
        console.error('Error collecting file:', error)
      }
    }

    return files
  },

  async fetchAsFile(url, element) {
    try {
      // Handle data URLs (base64 encoded images)
      if (url.startsWith('data:')) {
        return this.dataUrlToFile(url)
      }
      
      const response = await fetch(url, {
        mode: 'cors',
        credentials: 'omit'
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()
      let filename = this.extractFilename(url) || 'shared-file'
      
      // Ensure filename has an extension based on MIME type
      filename = this.ensureFileExtension(filename, blob.type)
      
      return new File([blob], filename, { type: blob.type || 'application/octet-stream' })
    } catch (error) {
      // Fallback for images: try canvas method
      if (element && element.tagName.toLowerCase() === 'img') {
        return this.imageElementToFile(element)
      }
      
      console.error('Error fetching file:', error)
      return null
    }
  },

  dataUrlToFile(dataUrl) {
    try {
      // Parse the data URL: data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...
      const [header, base64Data] = dataUrl.split(',')
      const mimeMatch = header.match(/data:([^;]+)/)
      const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream'
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], { type: mimeType })
      
      // Generate filename with proper extension
      const filename = this.ensureFileExtension('shared-image', mimeType)
      return new File([blob], filename, { type: mimeType })
    } catch (error) {
      console.error('Error converting data URL to file:', error)
      return null
    }
  },

  extractFilename(url) {
    try {
      const urlObj = new URL(url)
      const pathname = urlObj.pathname
      const filename = pathname.split('/').pop()
      
      // Clean up any query parameters from filename
      const cleanFilename = filename.split('?')[0]
      
      return cleanFilename || 'shared-file'
    } catch {
      return 'shared-file'
    }
  },
  
  ensureFileExtension(filename, mimeType) {
    // Check if filename already has an extension
    const hasExtension = filename.includes('.') && filename.split('.').pop().length <= 4
    
    if (hasExtension) {
      return filename
    }
    
    // Map MIME types to extensions
    const mimeToExt = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/svg+xml': '.svg',
      'video/mp4': '.mp4',
      'video/webm': '.webm',
      'application/pdf': '.pdf',
      'text/plain': '.txt',
      'application/zip': '.zip'
    }
    
    // Get extension from MIME type
    const ext = mimeToExt[mimeType] || '.png' // Default to .png for images
    return filename + ext
  },

  async imageElementToFile(imgElement) {
    try {
      // Create a canvas element
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      
      // Set canvas dimensions to match the image
      canvas.width = imgElement.naturalWidth || imgElement.width
      canvas.height = imgElement.naturalHeight || imgElement.height
      
      // Create a new image with crossOrigin attribute
      const corsImage = new Image()
      corsImage.crossOrigin = 'anonymous'
      
      return new Promise((resolve, reject) => {
        corsImage.onload = () => {
          try {
            // Draw the image to canvas
            ctx.drawImage(corsImage, 0, 0, canvas.width, canvas.height)
            
            // Convert canvas to blob
            canvas.toBlob((blob) => {
              if (blob) {
                let filename = imgElement.alt || this.extractFilename(imgElement.src) || 'shared-image'
                filename = this.ensureFileExtension(filename, blob.type)
                resolve(new File([blob], filename, { type: blob.type }))
              } else {
                reject(new Error('Failed to convert canvas to blob'))
              }
            }, 'image/png')
          } catch (error) {
            reject(error)
          }
        }
        
        corsImage.onerror = () => {
          reject(new Error('Failed to load image'))
        }
        
        // Try loading with a cache-busting parameter
        const separator = imgElement.src.includes('?') ? '&' : '?'
        corsImage.src = imgElement.src + separator + 'cors=' + Date.now()
      })
    } catch (error) {
      console.error('Error converting image element to file:', error)
      return null
    }
  }
  });
};